// src/utils/formatters.js

export const formatDistance = (meters) => {
    if (!meters) return '—';
    const miles = meters / 1609.344;
    return miles >= 0.1 ? `${miles.toFixed(2)} mi` : `${Math.round(meters)} m`;
};

export const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
        return `${hrs}h ${mins}m`;
    }
    return `${mins}m ${secs}s`;
};

export const formatPace = (secondsPerKm) => {
    if (!secondsPerKm) return '—';
    const secondsPerMile = secondsPerKm * 1.60934;
    const mins = Math.floor(secondsPerMile / 60);
    const secs = Math.round(secondsPerMile % 60);
    return `${mins}:${secs.toString().padStart(2, '0')} /mi`;
};

export const formatDate = (dateString, useRelative = true) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();

    if (useRelative) {
        const diffInSeconds = Math.floor((now - date) / 1000);
        if (diffInSeconds < 60) return 'just now';

        const diffInHours = diffInSeconds / 3600;
        if (diffInHours < 24) {
            if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
            return `${Math.floor(diffInHours)}h ago`;
        }
    }

    // For standard date format (used in activities)
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
};

export const formatTimeRelative = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;

    return date.toLocaleDateString();
};

export const formatElevation = (meters) => {
    if (!meters && meters !== 0) return null;
    return `${Math.round(meters * 3.28084)} ft`;
};

export const formatAltitude = (meters) => {
    if (!meters && meters !== 0) return '—';
    return `${Math.round(meters * 3.28084).toLocaleString()} ft`;
};

export const calculateSeriousAdjustedPace = (secondsPerKm, altitudeMeters, elevationGainMeters, distanceMeters) => {
    if (!secondsPerKm) return null;

    // 1. Grade Adjustment
    let gradeAdjustedPace = secondsPerKm;

    if (elevationGainMeters && distanceMeters && distanceMeters > 0) {
        const elevationGainFt = elevationGainMeters * 3.28084;
        // Rule: 10 seconds faster per mile for every 100 feet of elevation gain
        // Total Time Bonus = (Elevation Gain Ft / 100) * 10 seconds
        const totalTimeBonusSeconds = (elevationGainFt / 100) * 10;
        const distanceKm = distanceMeters / 1000;
        const paceBonusSecondsPerKm = totalTimeBonusSeconds / distanceKm;

        gradeAdjustedPace = secondsPerKm - paceBonusSecondsPerKm;
    }

    // 2. Altitude Adjustment
    // Rule: Add approximately 4–5 seconds per mile for every 1,000 feet of elevation above 3,000 ft
    // We'll use 4.5 seconds per mile as the average.

    // If no altitude data, return grade adjusted pace
    if (!altitudeMeters) return gradeAdjustedPace;

    const altitudeFeet = altitudeMeters * 3.28084;
    const baseAltitude = 3000;

    if (altitudeFeet <= baseAltitude) {
        return gradeAdjustedPace;
    }

    const feetAboveBase = altitudeFeet - baseAltitude;
    const thousandsAboveBase = feetAboveBase / 1000;

    // Calculate adjustment in seconds per mile
    const adjustmentSecondsPerMile = thousandsAboveBase * 4.5;

    // Convert to seconds per km to subtract from current pace
    const adjustmentSecondsPerKm = adjustmentSecondsPerMile / 1.60934;

    return gradeAdjustedPace - adjustmentSecondsPerKm;
};

export const formatAdjustedPace = (secondsPerKm, altitudeMeters, elevationGainMeters, distanceMeters) => {
    const adjustedPace = calculateSeriousAdjustedPace(secondsPerKm, altitudeMeters, elevationGainMeters, distanceMeters);
    if (!adjustedPace) return '—';

    const secondsPerMile = adjustedPace * 1.60934;
    const mins = Math.floor(secondsPerMile / 60);
    const secs = Math.round(secondsPerMile % 60);
    return `${mins}:${secs.toString().padStart(2, '0')} /mi`;
};

export const getRankClass = (score) => {
    if (!score && score !== 0) return 'rank-none';
    if (score >= 100) return 'rank-kipchoge';
    if (score >= 75) return 'rank-pro';
    if (score >= 50) return 'rank-serious';
    return 'rank-hobby';
};

export const getRankLabel = (score) => {
    if (!score && score !== 0) return '';
    if (score >= 100) return 'Kipchoge';
    if (score >= 75) return 'Pro';
    if (score >= 50) return 'Serious Runner';
    return 'Hobby Jogger';
};
